import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { cmsApi, apiError } from "../api";
import { ImageUpload } from "../components/ImageUpload";
import { usePageData, PageTitle, Card, TextField, Toggle, SaveBar, OrderButtons, moveItem, Spinner } from "../components/admin-ui";

const SECTION_LABELS = { services: "Ausgewählte Leistungen", promotions: "Angebote", gallery: "Galerie-Vorschau", image_sections: "Bildabschnitte", hours: "Öffnungszeiten & Standort", contact: "Treueprogramm-Hinweis" };

function CtaEditor({ label, cta, onChange, testid }) {
  return (
    <div className="rounded-xl border border-stone-200 p-4 space-y-3">
      <Toggle label={label} checked={cta.visible} onChange={(v) => onChange({ ...cta, visible: v })} testid={`${testid}-visible`} />
      <div className="grid sm:grid-cols-2 gap-3">
        <TextField label="Beschriftung" value={cta.label} onChange={(v) => onChange({ ...cta, label: v })} testid={`${testid}-label`} />
        <TextField label="Link / URL" value={cta.url} onChange={(v) => onChange({ ...cta, url: v })} placeholder="/kontakt oder https://…" testid={`${testid}-url`} />
      </div>
    </div>
  );
}

export default function HomepageAdmin() {
  const { data, loading } = usePageData("/website-admin/homepage");
  const { data: svcData } = usePageData("/website-admin/services");
  const { data: promos } = usePageData("/website-admin/promotions");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setForm((f) => f || { ...data, sections: [...(data.sections || [])].sort((a, b) => a.order - b.order) }); }, [data]);
  if (loading || !form) return <Spinner />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleId = (key, id) => set(key, form[key].includes(id) ? form[key].filter((x) => x !== id) : [...form[key], id]);
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, sections: form.sections.map((s, i) => ({ ...s, order: i })) };
      delete body.id; delete body.updated_at; delete body.updated_by;
      await cmsApi.put("/website-admin/homepage", body);
      toast.success("Startseite gespeichert");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="homepage-admin-page">
      <PageTitle title="Startseite" text="Texte, Hero-Bild, Buttons und Abschnitte der Startseite." />
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          <Card title="Hauptinhalt">
            <div className="space-y-4">
              <TextField label="Hauptüberschrift" value={form.title} onChange={(v) => set("title", v)} testid="home-title-input" />
              <TextField label="Untertitel" value={form.subtitle} onChange={(v) => set("subtitle", v)} testid="home-subtitle-input" />
              <TextField label="Einleitung" value={form.intro} onChange={(v) => set("intro", v)} textarea testid="home-intro-input" />
            </div>
          </Card>
          <Card title="Buttons (Call-to-Action)">
            <div className="space-y-4">
              <CtaEditor label="Primärer Button" cta={form.cta_primary} onChange={(v) => set("cta_primary", v)} testid="cta-primary" />
              <CtaEditor label="Sekundärer Button" cta={form.cta_secondary} onChange={(v) => set("cta_secondary", v)} testid="cta-secondary" />
            </div>
          </Card>
          <Card title="Bildabschnitte" text="Zusätzliche Bild-Text-Abschnitte auf der Startseite.">
            <div className="space-y-4">
              {form.image_sections.map((sec, i) => (
                <div key={i} className="rounded-xl border border-stone-200 p-4 grid sm:grid-cols-12 gap-4" data-testid={`image-section-${i}`}>
                  <div className="sm:col-span-5"><ImageUpload value={sec.media_id} kind="section" label="Bild" aspect="aspect-[4/3]" testid={`image-section-${i}-upload`} onChange={(id) => set("image_sections", form.image_sections.map((s, j) => (j === i ? { ...s, media_id: id } : s)))} /></div>
                  <div className="sm:col-span-7 space-y-3">
                    <TextField label="Titel" value={sec.title} onChange={(v) => set("image_sections", form.image_sections.map((s, j) => (j === i ? { ...s, title: v } : s)))} testid={`image-section-${i}-title`} />
                    <TextField label="Text" value={sec.text} onChange={(v) => set("image_sections", form.image_sections.map((s, j) => (j === i ? { ...s, text: v } : s)))} textarea rows={3} testid={`image-section-${i}-text`} />
                    <button onClick={() => set("image_sections", form.image_sections.filter((_, j) => j !== i))} className="inline-flex items-center gap-1 text-xs text-red-700" data-testid={`image-section-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /> Abschnitt entfernen</button>
                  </div>
                </div>
              ))}
              <button onClick={() => set("image_sections", [...form.image_sections, { media_id: null, title: "", text: "" }])} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A1D24]" data-testid="add-image-section"><Plus className="h-4 w-4" /> Abschnitt hinzufügen</button>
            </div>
          </Card>
        </div>
        <div className="lg:col-span-5 space-y-6">
          <Card title="Hero-Bild" text="Großes Bild im Kopfbereich (empfohlen: Querformat, mind. 1600 px). Standardmäßig ist ein Stock-Platzhalterfoto hinterlegt – bitte durch ein eigenes Salonfoto ersetzen.">
            <ImageUpload value={form.hero_media_id} onChange={(id) => set("hero_media_id", id)} kind="hero" label="" testid="hero-upload" />
          </Card>
          <Card title="Abschnitte" text="Sichtbarkeit und Reihenfolge der Startseiten-Abschnitte.">
            <ul className="space-y-1">
              {form.sections.map((s, i) => (
                <li key={s.key} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-[#FAF7F2]" data-testid={`section-row-${s.key}`}>
                  <OrderButtons testid={`section-${s.key}`} disableUp={i === 0} disableDown={i === form.sections.length - 1} onUp={() => set("sections", moveItem(form.sections, i, -1))} onDown={() => set("sections", moveItem(form.sections, i, 1))} />
                  <span className="flex-1 text-sm text-stone-800">{SECTION_LABELS[s.key] || s.key}</span>
                  <Toggle label="" checked={s.visible} onChange={(v) => set("sections", form.sections.map((x) => (x.key === s.key ? { ...x, visible: v } : x)))} testid={`section-${s.key}-visible`} />
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Hervorgehobene Leistungen" text="Werden auf der Startseite gezeigt. Ohne Auswahl erscheinen Leistungen mit Markierung „Hervorgehoben“.">
            <div className="max-h-64 overflow-y-auto space-y-1 pr-1" data-testid="featured-services-list">
              {(svcData?.services || []).filter((s) => s.active).map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                  <input type="checkbox" data-testid={`featured-service-${s.id}`} checked={form.featured_service_ids.includes(s.id)} onChange={() => toggleId("featured_service_ids", s.id)} className="accent-[#4A1D24]" /> {s.name}
                </label>
              ))}
            </div>
          </Card>
          <Card title="Hervorgehobene Angebote" text="Ohne Auswahl werden aktive Angebote mit Markierung „Hervorgehoben“ gezeigt.">
            <div className="max-h-48 overflow-y-auto space-y-1 pr-1" data-testid="featured-promos-list">
              {(promos || []).length === 0 && <p className="text-xs text-stone-400">Noch keine Angebote angelegt.</p>}
              {(promos || []).map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                  <input type="checkbox" data-testid={`featured-promo-${p.id}`} checked={form.featured_promotion_ids.includes(p.id)} onChange={() => toggleId("featured_promotion_ids", p.id)} className="accent-[#4A1D24]" /> {p.title}
                </label>
              ))}
            </div>
          </Card>
        </div>
      </div>
      <SaveBar onSave={save} saving={saving} testid="homepage-save" />
    </div>
  );
}
